import { DialogContent, DialogOverlay } from '@reach/dialog'
import React from 'react'
import { animated, useSpring, useTransition } from 'react-spring'
import { useGesture } from 'react-use-gesture'
import styled, { css } from 'styled-components'

import { isMobile } from '../../utils/userAgent'

const AnimatedDialogOverlay = animated(DialogOverlay)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StyledDialogOverlay = styled(AnimatedDialogOverlay)`
  &[data-reach-dialog-overlay] {
    z-index: 2;
    background-color: transparent;
    overflow: hidden;

    display: flex;
    align-items: center;
    justify-content: center;

    background-color: ${({ theme }) => theme.deprecated_modalBG};
  }
`

const AnimatedDialogContent = animated(DialogContent)
// destructure to not pass custom props to Dialog DOM element
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StyledDialogContent = styled(({ minHeight, maxHeight, maxWidth, mobile, isOpen, borderRadius, ...rest }) => (
  <AnimatedDialogContent {...rest} />
)).attrs({
  'aria-label': 'dialog',
})`
  overflow-y: auto;

  &[data-reach-dialog-content] {
    margin: 0 0 2rem 0;
    background-color: ${({ theme }) => theme.cardPrimary};
    border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
    box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.09);
    padding: 24px;
    width: 50vw;
    overflow-y: auto;
    overflow-x: hidden;

    // align-self: ${({ mobile }) => (mobile ? 'flex-end' : 'center')};

    max-width: ${({ maxWidth }) => `${maxWidth}px`}; // 446px
    ${({ maxHeight }) =>
      maxHeight &&
      css`
        max-height: ${maxHeight}vh;
      `}
    ${({ minHeight }) =>
      minHeight &&
      css`
        min-height: ${minHeight}vh;
      `}
    display: flex;
    border-radius: ${({ borderRadius }) => borderRadius}
      ${({ theme }) => theme.mediaWidth.upToMedium`
      width: 65vw;
      margin: 0;
    `}
      ${({ theme, mobile, borderRadius }) => theme.mediaWidth.upToSmall`
      width:  85vw;
      ${
        mobile &&
        css`
          width: 100vw;
          border-radius: ${borderRadius};
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        `
      }
    `};
  }
`

interface ModalProps {
  isOpen: boolean
  onDismiss: () => void
  minHeight?: number | false
  maxHeight?: number
  maxWidth?: number
  initialFocusRef?: React.RefObject<any>
  children?: React.ReactNode
  disableDismiss?: boolean
  borderRadius?: string
  withoutAnimation?: boolean
}

export default function Modal({
  isOpen,
  onDismiss,
  minHeight = false,
  maxHeight = 90,
  maxWidth = 446,
  initialFocusRef,
  children,
  disableDismiss = false,
  borderRadius = '8px',
  withoutAnimation = false,
}: ModalProps) {
  const handleDismiss = () => {
    if (!disableDismiss) {
      onDismiss()
    }
  }

  const fadeTransition = useTransition(isOpen, {
    config: { duration: withoutAnimation ? 0 : 200 },
    from: { opacity: withoutAnimation ? 1 : 0 },
    enter: { opacity: 1 },
    leave: { opacity: withoutAnimation ? 1 : 0 },
  })

  const [{ y }, set] = useSpring(() => ({
    y: 0,
    config: withoutAnimation ? { duration: 0 } : { mass: 1, tension: 210, friction: 20 },
  }))

  const bind = useGesture({
    onDrag: (state) => {
      if (!withoutAnimation) {
        set({
          y: state.down ? state.movement[1] : 0,
        })
      }
      if (state.movement[1] > 300 || (state.velocity > 3 && state.direction[1] > 0)) {
        onDismiss()
      }
    },
  })

  const dialogBody = (key: number, props?: any) => (
    <StyledDialogOverlay
      key={key}
      style={withoutAnimation ? undefined : props}
      onDismiss={handleDismiss}
      initialFocusRef={initialFocusRef}
      unstable_lockFocusAcrossFrames={false}
    >
      <StyledDialogContent
        {...(!withoutAnimation && isMobile && y
          ? {
              ...bind(),
              style: { transform: y.interpolate((y) => `translateY(${(y as number) > 0 ? y : 0}px)`) },
            }
          : {})}
        aria-label="dialog content"
        minHeight={minHeight}
        maxHeight={maxHeight}
        maxWidth={maxWidth}
        mobile={isMobile}
        className="modal-content"
        borderRadius={borderRadius}
      >
        {/* prevents the automatic focusing of inputs on mobile by the reach dialog */}
        {!initialFocusRef && isMobile ? <div tabIndex={1} /> : null}
        {children}
      </StyledDialogContent>
    </StyledDialogOverlay>
  )

  if (withoutAnimation) {
    return isOpen ? dialogBody(0) : null
  }

  return <>{fadeTransition((props, item, _, key) => item && dialogBody(key, props))}</>
}
